import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, SimpleChanges, ViewEncapsulation, ViewChild, inject } from '@angular/core';
import Splide from '@splidejs/splide';

@Component({
    selector: 'app-splide-slider',
    imports: [CommonModule],
    templateUrl: './splide-slider.component.html',
    styleUrl: './splide-slider.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class SplideSliderComponent implements OnChanges, OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('splide', { static: true }) splideEl!: ElementRef<HTMLElement>;
  @Input() itemsCount = 0;
  @Input() showArrows = true;
  @Input() paddingInline = true;

  public centerSlides = false;

  private splide: Splide | null = null;

  ngOnChanges(changes: SimpleChanges) {
    const itemsCountChange = changes['itemsCount']?.currentValue > 0;

    const showArrowsChange =
      changes['showArrows'] && !changes['showArrows'].firstChange;

    const gapChange = changes['gap'] && !changes['gap'].firstChange;

    const list = this.splideEl.nativeElement.querySelector('.splide__list');

    if ((itemsCountChange || showArrowsChange || gapChange) && list) {
      this.destroySlider();

      // Run Splide outside Angular so its synchronous lifecycle events
      // (mounted/moved/resized) cannot mutate component state mid-CD.
      this.zone.runOutsideAngular(() => {
        const observer = new MutationObserver(() => {
          const slides = list.querySelectorAll('.splide__slide');

          if (slides.length === 0) {
            return;
          }

          observer.disconnect();

          this.splide = new Splide(this.splideEl.nativeElement, {
            type: 'slide',
            perPage: 4,
            gap: '1rem',
            pagination: true,
            arrows: this.showArrows,
            breakpoints: {
              1024: { perPage: 3 },
              768: { perPage: 2 },
              576: { perPage: 1 },
            },
          });

          this.splide.on('mounted moved updated resized', () => {
            const next = (this.splide!.options.perPage ?? 0) > slides.length;
            if (next === this.centerSlides) {
              return;
            }
            // Re-enter the zone on the next microtask so the assignment
            // never races with the CD pass that triggered the event.
            queueMicrotask(() =>
              this.zone.run(() => {
                this.centerSlides = next;
                this.cdr.markForCheck();
              }),
            );
          });

          this.splide.mount();
        });

        observer.observe(list, { childList: true });
      });
    }
  }

  ngOnDestroy(): void {
    this.destroySlider();
  }

  private destroySlider(): void {
    this.splide?.destroy(true);
    this.splide = null;
  }
}
